import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send, RefreshCcw, UserCheck, History } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase CINV — Customer Portal Invite
 *
 * Two surfaces composed from this file:
 *
 *  1. ``PortalInviteHeaderControl`` — sits in the customer-detail header.
 *     Renders one of three states:
 *       a) Active linked customer → green chip "User: <email> (active)".
 *       b) No invite or prior invite expired/used → "Invite to portal" button.
 *       c) Prior invite still active (sent + not expired) → "Resend invite"
 *          button + relative-time subtext. Resend opens a confirmation
 *          dialog before firing.
 *
 *  2. ``PortalAccessTab`` — full tab body. Renders the linked-user row +
 *     invite-history table + the same Invite/Resend button.
 *
 * Both surfaces share state via the ``usePortalInvites(customerId)`` hook
 * exported here so the header and tab stay in sync after issuance.
 */

function fmtTime(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
}

function relTime(s) {
    if (!s) return "";
    try {
        const t = new Date(s).getTime();
        const diff = Math.max(0, Date.now() - t);
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    } catch { return ""; }
}

const STATUS_PILL = {
    sent: "bg-blue-50 text-blue-900",
    used: "bg-emerald-50 text-emerald-800",
    expired: "bg-amber-50 text-amber-900",
    revoked: "bg-slate-100 text-slate-600",
};

const STATUS_LABEL = {
    sent: "Sent",
    used: "Used",
    expired: "Expired",
    revoked: "Revoked",
};

export function usePortalInvites(customerId) {
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const reload = useCallback(async () => {
        if (!customerId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/customers/${customerId}/portal-invites`);
            setInvites(data || []);
        } catch {
            // History endpoint is non-critical — fail soft. The action
            // button still works without history (it'll just show "no
            // prior invite" state).
            setInvites([]);
        } finally {
            setLoading(false);
        }
    }, [customerId]);
    useEffect(() => { reload(); }, [reload]);
    return { invites, loading, reload };
}

/** Returns the most recent invite if it's still actionable (sent + not expired). */
function activeInvite(invites) {
    if (!invites || !invites.length) return null;
    const top = invites[0]; // newest first per API contract
    if (top.status !== "sent") return null;
    if (!top.expires_at) return top;
    try {
        if (new Date(top.expires_at).getTime() < Date.now()) return null;
    } catch { /* ignore */ }
    return top;
}

/** Returns the first linked user that has activated (not must_reset_password). */
function findActiveLinkedUser(customer) {
    const linked = customer?.linked_users || [];
    if (!linked.length) return null;
    // ``linked_users`` is a list of {id, email, full_name, must_reset_password}
    // when hydrated by GET /admin/customers/{id}. The "active" gate is the
    // server-side source of truth (POST /portal-invite returns 409 only
    // when a non-must_reset_password user is linked), so mirror that here.
    return linked.find(
        (u) => u && typeof u === "object" && u.email && !u.must_reset_password,
    ) || null;
}

function ConfirmResendDialog({ open, onOpenChange, onConfirm, busy }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="portal-invite-confirm-resend">
                <DialogHeader>
                    <DialogTitle>Resend portal invite?</DialogTitle>
                    <DialogDescription>
                        A previous invite is still active and will be invalidated.
                        The customer will need to use the new link instead.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        data-testid="portal-invite-confirm-cancel"
                    >Cancel</Button>
                    <Button
                        onClick={onConfirm}
                        disabled={busy}
                        data-testid="portal-invite-confirm-send"
                        className="bg-blue-900 hover:bg-blue-800 text-white"
                    >
                        {busy ? "Sending…" : "Resend invite"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function PortalInviteHeaderControl({ customer, invites, onChange }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const hasEmail = !!(customer?.billing_contact_email || customer?.guarantor_email);
    const activeUser = findActiveLinkedUser(customer);
    const active = activeInvite(invites);

    // State A — linked + active user: green chip (no button).
    if (activeUser) {
        return (
            <div
                className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800 border border-emerald-200"
                data-testid="customer-portal-user-chip"
                title={`Portal user: ${activeUser.email}`}
            >
                <UserCheck size={14} className="text-emerald-700" />
                <span data-testid="customer-portal-user-email">User: {activeUser.email}</span>
                <span className="text-emerald-700">(active)</span>
            </div>
        );
    }

    const issue = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/customers/${customer.id}/portal-invite`);
            toast.success(`Portal invite sent to ${data.sent_to}`);
            setConfirmOpen(false);
            await onChange?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const label = active ? "Resend invite" : "Invite to portal";
    const onClick = () => {
        if (!hasEmail) return; // disabled state handles UX
        if (active) setConfirmOpen(true);
        else issue();
    };

    return (
        <>
            <div className="flex flex-col items-end">
                <Button
                    onClick={onClick}
                    disabled={!hasEmail || busy}
                    title={!hasEmail ? "Add a contact email before inviting" : undefined}
                    className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                    data-testid="customer-portal-invite-button"
                >
                    {active ? <RefreshCcw size={14} /> : <Send size={14} />}
                    {busy ? "Sending…" : label}
                </Button>
                {active && (
                    <p
                        className="mt-1 text-[11px] text-slate-500"
                        data-testid="customer-portal-invite-relative-time"
                    >Sent {relTime(active.sent_at)}</p>
                )}
            </div>
            <ConfirmResendDialog
                open={confirmOpen}
                onOpenChange={(v) => !busy && setConfirmOpen(v)}
                onConfirm={issue}
                busy={busy}
            />
        </>
    );
}

export function PortalAccessTab({ customer, invites, loading, onChange }) {
    const activeUser = findActiveLinkedUser(customer);
    return (
        <div className="space-y-6" data-testid="customer-portal-access-tab">
            <div>
                <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500 mb-3">
                    Linked portal user
                </h3>
                {activeUser ? (
                    <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50/40 rounded-md px-4 py-3" data-testid="customer-portal-linked-user-row">
                        <div>
                            <div className="font-medium text-sm text-slate-900">{activeUser.full_name || activeUser.email}</div>
                            <div className="text-xs text-slate-600">{activeUser.email}</div>
                        </div>
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800">
                            <UserCheck size={14} /> Active
                        </span>
                    </div>
                ) : (
                    <p className="text-sm text-slate-500" data-testid="customer-portal-no-linked-user">
                        No portal account yet. Send an invite below.
                    </p>
                )}
            </div>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">
                        Invite history
                    </h3>
                    <PortalInviteHeaderControl
                        customer={customer}
                        invites={invites}
                        onChange={onChange}
                    />
                </div>
                {loading && (
                    <p className="text-sm text-slate-500" data-testid="customer-portal-history-loading">
                        Loading invite history…
                    </p>
                )}
                {!loading && invites.length === 0 && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 rounded-md border border-dashed border-slate-200 p-4" data-testid="customer-portal-history-empty">
                        <History size={14} className="text-slate-400" />
                        No invites sent yet.
                    </div>
                )}
                {!loading && invites.length > 0 && (
                    <div className="overflow-x-auto" data-testid="customer-portal-history-table">
                        <table className="min-w-full text-sm">
                            <thead className="text-xs uppercase tracking-wider text-slate-500">
                                <tr className="border-b border-slate-200">
                                    <th className="text-left py-2 pr-3 font-medium">Sent at</th>
                                    <th className="text-left py-2 pr-3 font-medium">Sent to</th>
                                    <th className="text-left py-2 pr-3 font-medium">Sent by</th>
                                    <th className="text-left py-2 pr-3 font-medium">Status</th>
                                    <th className="text-left py-2 font-medium">Resolved</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {invites.map((iv) => {
                                    const resolvedTs =
                                        iv.used_at || iv.revoked_at || iv.expires_at;
                                    const resolvedLabel =
                                        iv.status === "used" ? `Used ${fmtTime(iv.used_at)}` :
                                        iv.status === "revoked" ? `Revoked ${fmtTime(iv.revoked_at)}` :
                                        iv.status === "expired" ? `Expired ${fmtTime(iv.expires_at)}` :
                                        `Expires ${fmtTime(iv.expires_at)}`;
                                    return (
                                        <tr
                                            key={iv.id}
                                            data-testid={`customer-portal-history-row-${iv.id}`}
                                        >
                                            <td className="py-2.5 pr-3 text-slate-700 align-top">{fmtTime(iv.sent_at)}</td>
                                            <td className="py-2.5 pr-3 text-slate-700 align-top">{iv.sent_to}</td>
                                            <td className="py-2.5 pr-3 text-slate-700 align-top">{iv.sent_by_name || "—"}</td>
                                            <td className="py-2.5 pr-3 align-top">
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_PILL[iv.status] || "bg-slate-100 text-slate-700"}`}
                                                    data-testid={`customer-portal-history-status-${iv.id}`}
                                                >
                                                    {STATUS_LABEL[iv.status] || iv.status}
                                                </span>
                                                {iv.status === "revoked" && iv.revoked_reason && (
                                                    <p className="text-[11px] text-slate-500 mt-1">{iv.revoked_reason.replace(/_/g, " ")}</p>
                                                )}
                                            </td>
                                            <td className="py-2.5 text-slate-600 align-top text-xs">{resolvedTs ? resolvedLabel : "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
